import { triggerTracking } from '@zoopla/tracking';
import { ButtonLeft } from '@zoopla-eng/zds/button';
import { Divider } from '@zoopla-eng/zds/divider';
import { EmailMedium, SaveMedium } from '@zoopla-eng/zds/icons';
import { Link } from '@zoopla-eng/zds/link';
import { Stack } from '@zoopla-eng/zds/stack';
import { AnalyticsTaxonomy } from '@zoopla/listing-search-context';
import { useGetMapViewUrl } from '@zoopla/listing-search/use-map-view-url';
import { useIsLoggedIn, useUser } from '@zoopla/user';
import {
    ANALYTICS_BASE_TAXONOMY,
    Taxonomy,
    updateUserTaxonomyKeys,
    UserTaxonomy,
    useTrackSegmentEvent
} from '@zoopla/tracking';
import { FC, useRef } from 'react';
import {
    trackCreateEmailAlert,
    trackSaveThisSearch
} from './SaveThisSearchModal.tracking';
import { SaveThisSearchModalWithAuth } from './SaveThisSearchModalWithAuth';
import { useSaveThisSearchModal } from './useSaveThisSearchModal';
import { saveEmailAlertModalStyle } from './AlertPanel.styles.css';

type AlertPanelProps = {
    analyticsTaxonomy?: AnalyticsTaxonomy;
};

export const AlertPanel: FC<React.PropsWithChildren<AlertPanelProps>> = ({
    analyticsTaxonomy
}) => {
    const isLoggedIn = useIsLoggedIn();

    const {
        showAuthModal,
        setShowAuthModal,
        defaultAlertFrequency,
        setDefaultAlertFrequency,
        showSaveThisSearchModal,
        setShowSaveThisSearchModal
    } = useSaveThisSearchModal();

    const timeTravelSearchHref = '/travel-time/';
    const drawYourSearchHref = useGetMapViewUrl(true);

    const userTaxonomy = useUser();
    const trackSegmentEvent = useTrackSegmentEvent();

    const container = useRef<HTMLDivElement>(null);

    return (
        <div className={saveEmailAlertModalStyle} ref={container}>
            <Stack rowGap="S400">
                <ButtonLeft
                    category="secondary"
                    Icon={EmailMedium}
                    onClick={() => {
                        setDefaultAlertFrequency('0');

                        trackCreateEmailAlert();

                        trackSegmentEvent(
                            'Click Email Alert',
                            {
                                ...ANALYTICS_BASE_TAXONOMY,
                                ...(analyticsTaxonomy as unknown as Taxonomy),
                                ...(updateUserTaxonomyKeys(
                                    userTaxonomy
                                ) as unknown as UserTaxonomy),
                                signedInStatus: isLoggedIn
                                    ? 'signed in'
                                    : 'signed out',
                                location: analyticsTaxonomy?.areaName
                            },
                            undefined
                        );

                        if (!isLoggedIn) {
                            setShowAuthModal(true);
                        } else {
                            setShowSaveThisSearchModal(true);
                        }
                    }}
                >
                    Create email alert
                </ButtonLeft>

                <ButtonLeft
                    category="secondary"
                    Icon={SaveMedium}
                    onClick={() => {
                        setDefaultAlertFrequency('');

                        trackSaveThisSearch();

                        trackSegmentEvent(
                            'Click Save Search',
                            {
                                ...ANALYTICS_BASE_TAXONOMY,
                                ...(analyticsTaxonomy as unknown as Taxonomy),
                                ...(updateUserTaxonomyKeys(
                                    userTaxonomy
                                ) as unknown as UserTaxonomy),
                                signedInStatus: isLoggedIn
                                    ? 'signed in'
                                    : 'signed out',
                                location: analyticsTaxonomy?.areaName
                            },
                            undefined
                        );

                        if (!isLoggedIn) {
                            setShowAuthModal(true);
                        } else {
                            setShowSaveThisSearchModal(true);
                        }
                    }}
                >
                    Save this search
                </ButtonLeft>

                <Divider direction="horizontal" />
                <Link href={timeTravelSearchHref}>Travel time search</Link>
                <Link
                    onClick={() => {
                        triggerTracking({
                            event: 'GAEvent',
                            eventCategory: 'search results',
                            eventAction: 'clickthrough',
                            eventLabel: 'draw_search'
                        });
                    }}
                    href={drawYourSearchHref}
                >
                    Draw your search
                </Link>
                <SaveThisSearchModalWithAuth
                    {...{
                        showAuthModal,
                        setShowAuthModal,
                        defaultAlertFrequency,
                        setDefaultAlertFrequency,
                        showSaveThisSearchModal,
                        setShowSaveThisSearchModal,
                        container
                    }}
                />
            </Stack>
        </div>
    );
};
